import { notify } from "./feedback.ts";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { ArrowsClockwiseIcon, CaretDownIcon, DesktopIcon, LinkBreakIcon, PlayIcon, XIcon } from "@phosphor-icons/react";
import { Menu } from "@base-ui/react/menu";
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import type * as C from "@zooi/contracts";
import { q } from "./api.ts";
import { COMMON_LABELS, LOCAL_LAUNCHER_LABELS as L } from "./labels.ts";
import { beginLauncherSetup, completeLauncherSetup, launcherHref, launchSurface, pairingReturn, parsePairing, readPairing, subscribePairing, writePairing, type LauncherHost, type LauncherPairing, type LauncherTask } from "./launcher-pairing.ts";
import { Button, Field, Section, ViewSwitch } from "./ui.tsx";
import { AgentLogo } from "./ui/props.tsx";
import { readPref, writePref } from "./prefs.ts";

function usePairing(scope: string, productId: string) {
  const raw = useSyncExternalStore(subscribePairing, () => readPairing(scope, productId), () => null);
  return useMemo(() => raw ? parsePairing(raw, productId, window.location.origin) : null, [raw, productId]);
}

const appName = (host: LauncherHost) => host === "codex" ? "Codex" : "Claude";

function RunButtons({ pairing }: { pairing: LauncherPairing }) {
  return <div className="z-flow z-launcher-run">
    <div className="z-inline">{pairing.hosts.map((host) => <Button key={host} onClick={() => {
      window.location.assign(launcherHref(pairing, host));
      notify.info(launchSurface(pairing, host) === "app" ? L.appRequested(appName(host)) : L.requested);
    }}><AgentLogo engine={host === "claude" ? "claude_code" : "codex"} />{host === "claude" ? L.runClaude : L.runCodex}</Button>)}</div>
    <p className="z-small z-muted">{!pairing.apps ? L.runNote : pairing.apps.length === pairing.hosts.length ? L.appRunNote : L.mixedRunNote}</p>
  </div>;
}

/** Product-level entry point for an entire run, scoped to this browser and person. */
export function LauncherRun({ productId }: { productId: string }) {
  const me = useQuery(q.me);
  const scope = me.data ? `${me.data.workspace.id}:${me.data.user.id}` : "";
  const pairing = usePairing(scope, productId);
  return pairing && me.data ? <RunButtons key={pairing.id} pairing={pairing} /> : <p><Link className="z-btn" to="/settings/products/$productId/agent" params={{ productId }} hash="local-launcher"><DesktopIcon size={16} aria-hidden />{L.setup}</Link></p>;
}

/** The fixed skill each task starts, as the person would type it. */
const TASK_SKILL: Record<LauncherTask, string> = { data_setup: "zooi:connect" };

/**
 * One object-level action, or one fixed task such as data setup; setup and
 * manual handoff stay in the arrow menu.
 */
export function ActionLaunchControl({ productId, workOrderId, task, compact = false }: { productId: string; workOrderId?: string; task?: LauncherTask; compact?: boolean }) {
  const navigate = useNavigate();
  const me = useQuery(q.me);
  const scope = me.data ? `${me.data.workspace.id}:${me.data.user.id}` : "";
  const pairing = usePairing(scope, productId);
  const [requested, setRequested] = useState<LauncherHost | null>(null);
  const key = `launcher-host:${scope}:${productId}`;
  const preferred = requested ?? readPref(key);
  const ready = Boolean((task ? pairing?.tasks?.includes(task) : pairing?.work_orders) && me.data);
  const hosts = pairing?.hosts ?? [];
  const selected = hosts.find((host) => host === preferred) ?? hosts.find((host) => host === "codex") ?? hosts[0];
  const launch = (host: LauncherHost) => {
    if (!ready || !pairing) return;
    window.location.assign(launcherHref(pairing, host, workOrderId, task));
    writePref(key, host); setRequested(host);
    if (launchSurface(pairing, host) === "app") notify.info((task ? L.appTaskRequested : L.appActionRequested)(appName(host)));
    else notify.info((task ? L.taskRequested : L.actionRequested)(host === "codex" ? "Codex" : "Claude Code"));
  };
  const copy = async (host: LauncherHost) => {
    try { await navigator.clipboard.writeText(`${host === "codex" ? "$" : "/"}${task ? TASK_SKILL[task] : `zooi:do ${workOrderId}`}`); notify.success(COMMON_LABELS.copied); }
    catch { notify.error(COMMON_LABELS.copyFailed); }
  };
  return <div className="z-action-launch" id={task ? `task-${task}` : "action-execution"}>
    {ready && selected && <Button tone="primary" aria-label={selected === "codex" ? L.executeCodex : L.executeClaude} onClick={() => launch(selected)}>
      <AgentLogo engine={selected === "codex" ? "codex" : "claude_code"} />{compact ? (task ? L.executeTask : L.execute) : selected === "codex" ? L.executeCodex : L.executeClaude}
    </Button>}
    <Menu.Root>
      <Menu.Trigger className={`z-btn primary${ready ? " z-action-launch-picker" : ""}`} aria-label={L.executionOptions}>
        {!ready && <><PlayIcon size={14} aria-hidden />{task ? L.executeTask : L.execute}</>}<CaretDownIcon size={12} aria-hidden />
      </Menu.Trigger>
      <Menu.Portal><Menu.Positioner sideOffset={4} align="end"><Menu.Popup className="z-layer">
        {ready && <>{hosts.map((host) => <Menu.Item key={host} className="z-menu-item" onClick={() => launch(host)}>
          <AgentLogo engine={host === "codex" ? "codex" : "claude_code"} />{host === "codex" ? L.executeCodex : L.executeClaude}
        </Menu.Item>)}<div className="z-menu-sep" /></>}
        {!ready && <p className="z-action-launch-note">{pairing ? (task ? L.updateTaskNote : L.updateNote) : L.setupNote}</p>}
        {(["codex", "claude"] as const).map((host) => <Menu.Item key={host} className="z-menu-item" onClick={() => void copy(host)}>
          <AgentLogo engine={host === "codex" ? "codex" : "claude_code"} />{host === "codex" ? L.copyCodex : L.copyClaude}
        </Menu.Item>)}
        <div className="z-menu-sep" />
        <Menu.Item className="z-menu-item" onClick={() => void navigate({ to: "/settings/products/$productId/agent", params: { productId }, hash: "local-launcher" })}>
          <DesktopIcon size={16} aria-hidden />{pairing && !ready ? L.update : L.launcherSettings}
        </Menu.Item>
      </Menu.Popup></Menu.Positioner></Menu.Portal>
    </Menu.Root>
  </div>;
}

/** Setup returns are automatic only after this person copied a scoped setup prompt. */
export function LocalLauncher({ scope, product }: { scope: string; product: C.Product }) {
  const productId = product.id;
  const pairing = usePairing(scope, productId);
  const [incoming, setIncoming] = useState(() => pairingReturn(window.location.hash));
  const [code, setCode] = useState(incoming?.code ?? "");
  const [setup, setSetup] = useState(false);
  const [host, setHost] = useState<LauncherHost>("claude");
  const [setupId, setSetupId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");
  const handled = useRef<typeof incoming>(null);
  useEffect(() => {
    // The OS may reuse this tab and navigate only its fragment.
    const receive = () => {
      if (!window.location.hash.startsWith("#zooi-launcher=")) return;
      const next = pairingReturn(window.location.hash);
      setIncoming(next); setCode(next?.code ?? ""); setError(next ? "" : L.invalid);
    };
    window.addEventListener("hashchange", receive);
    return () => window.removeEventListener("hashchange", receive);
  }, []);
  useEffect(() => {
    if (window.location.hash.startsWith("#zooi-launcher=")) window.history.replaceState(window.history.state, "", `${window.location.pathname}${window.location.search}#local-launcher`);
    if (!incoming || handled.current === incoming) return;
    handled.current = incoming;
    const parsed = parsePairing(incoming.code, productId, window.location.origin);
    if (!parsed) { setError(L.invalid); return; }
    if (completeLauncherSetup(scope, productId, incoming.code, window.location.origin, incoming.setupId)) {
      setCode(""); setSetup(false); notify.success(L.paired);
    } else setSetup(true);
  }, [incoming, scope, productId]);
  const save = () => {
    const parsed = parsePairing(code.trim(), productId, window.location.origin);
    if (!parsed) { setError(L.invalid); return; }
    if (!writePairing(scope, productId, parsed)) { setError(L.storageFailed); return; }
    setCode(""); setError(""); setSetup(false);
  };
  const copy = async (selected: LauncherHost) => {
    const id = beginLauncherSetup(scope, productId);
    setHost(selected); setSetupId(id); setCopied(false); setError("");
    try {
      await navigator.clipboard.writeText(L.setupPrompt(product, window.location.origin, selected, id));
      setCopied(true); notify.success(COMMON_LABELS.copied);
    } catch { notify.error(COMMON_LABELS.copyFailed); }
  };
  const copyUpdate = async () => {
    if (!pairing) return;
    const id = beginLauncherSetup(scope, productId);
    setCopied(false); setError("");
    try {
      await navigator.clipboard.writeText(L.upgradePrompt(product, window.location.origin, pairing.hosts, id));
      setCopied(true); notify.success(COMMON_LABELS.copied);
    } catch { notify.error(COMMON_LABELS.copyFailed); }
  };
  const returned = incoming && code === incoming.code && parsePairing(code, productId, window.location.origin);
  return <Section id="local-launcher" title={pairing ? L.title : L.install} actions={pairing && <Button size="sm" tone="ghost" icon={setup ? <XIcon /> : <ArrowsClockwiseIcon />} aria-expanded={setup} onClick={() => setSetup(!setup)}>{setup ? L.close : L.pairAgain}</Button>}>
    {pairing && !setup ? <div className="z-flow">
      <p className="z-small z-muted" role="status">{L.paired}</p>
      <RunButtons key={pairing.id} pairing={pairing} />
      {!pairing.work_orders ? <div className="z-flow">
        <p className="z-small z-muted">{L.updateNote}</p>
        <p><Button onClick={() => void copyUpdate()}><DesktopIcon size={16} aria-hidden />{L.copyLauncherUpdate}</Button></p>
        {copied && <p className="z-small z-muted" role="status">{L.waiting}</p>}
      </div> : null}
      <p><Button size="sm" tone="ghost" icon={<LinkBreakIcon />} onClick={() => {
        if (!writePairing(scope, productId, null)) setError(L.storageFailed);
      }}>{L.disconnect}</Button></p>
    </div> : <div className="z-edit-block z-flow">
      {returned ? <>
        <p>{L.confirmNote}</p>
        <p><Button tone="primary" onClick={save}>{L.confirm}</Button></p>
      </> : <>
        <p>{L.note}</p>
        <div className="z-inline">
          <Button onClick={() => void copy("claude")}><AgentLogo engine="claude_code" />{L.copyClaude}</Button>
          <Button onClick={() => void copy("codex")}><AgentLogo engine="codex" />{L.copyCodex}</Button>
        </div>
        <p className="z-small z-muted">{L.personalSteps}</p>
        {copied && <p className="z-small" role="status">{L.waiting}</p>}
        <details className="z-disclosure">
          <summary>{L.preview}</summary>
          <div className="z-flow">
            <ViewSwitch label={L.client} value={host} onValueChange={setHost} options={[
              { value: "claude", label: "Claude Code", icon: <AgentLogo engine="claude_code" size={14} /> },
              { value: "codex", label: "Codex", icon: <AgentLogo engine="codex" size={14} /> },
            ]} />
            <div className="z-cli"><pre>{L.setupPrompt(product, window.location.origin, host, setupId)}</pre></div>
          </div>
        </details>
      </>}
      <details className="z-disclosure">
        <summary>{L.recovery}</summary>
        <form className="z-flow" onSubmit={(event) => { event.preventDefault(); save(); }}>
          <p className="z-small z-muted">{L.recoveryNote}</p>
          <Field label={L.code} help={L.codeNote}><textarea className="z-input z-mono" rows={3} value={code} autoComplete="off" spellCheck={false} onChange={(event) => { setCode(event.target.value); setError(""); }} /></Field>
          <p><Button type="submit" tone="primary" disabled={!code.trim()}>{L.save}</Button></p>
        </form>
      </details>
    </div>}
    {error && <p className="z-small" role="alert">{error}</p>}
  </Section>;
}
